import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { theme, Icon, Button, Select, message } from 'components';
import {
  ParsedSupraLockboxJsonType,
  updateOwnerOrAssignee,
} from 'api/SupraLockboxController';
import { debounce } from 'lodash';
import styled from 'styled-components';
import Axios from 'axios';
import { Modal } from 'react-bootstrap';
import { Loading } from '@app/react-common';
import { buildLockboxAssignConfirmMessage, featureEnabled } from 'utils';
import { OwnerAndAssigneeType } from '@bb/domain/dist/types/SupraLockbox';
import SearchLabel from './SearchLabel';

type SearchBarProps = {
  disabled?: boolean;
  isMlsUser: boolean;
  lockbox: ParsedSupraLockboxJsonType;
  fetchLockboxData: any;
};

type OwnerValueType = {
  id: string;
  type: string;
  label: JSX.Element;
};

const OwnerSearchBar: React.FC<SearchBarProps> = ({
  isMlsUser,
  lockbox,
  fetchLockboxData,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [value, setValue] = useState<OwnerValueType | null>();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmReassignVisible, setConfirmReassignVisible] = useState(false);
  const [ownerType, setOwnerType] = useState<'user' | 'branch' | 'office' | ''>(
    '',
  );
  const prevOwnerValue = useRef<OwnerValueType | null>(null);
  const newOwnerValue = useRef('');

  // Clear search results when ownerType changes
  useEffect(() => {
    if (featureEnabled('improvedLockboxAssignmentFeature')) setData([]);
  }, [ownerType]);

  useEffect(() => {
    if (!lockbox || !lockbox.owner || !lockbox.parsedOwner) return;

    setData([{ id: lockbox.owner, ...lockbox.parsedOwner }]);
    setValue({
      id: lockbox.parsedOwner.id ?? lockbox.owner,
      type: lockbox.parsedOwner.type,
      label: SearchLabel(lockbox.parsedOwner, isMlsUser),
    });
  }, [lockbox]);

  const handleError = (err: any) => {
    let errorMsg: string = err.response?.data?.message || err.message;
    if (errorMsg.includes('not authorized to')) {
      // Only show last error
      const errorMsgs = errorMsg.split('BrokerBayForbiddenError: ');
      errorMsg = errorMsgs[errorMsgs.length - 1];
    }
    message.error(errorMsg);
  };

  const unassignLockbox = async () => {
    try {
      await updateOwnerOrAssignee({
        serialNumber: lockbox.serialNumber,
        ownerId: prevOwnerValue.current?.id,
        ownerType: prevOwnerValue.current?.type,
        updateType: 'unassign',
      });
      if (lockbox._id) {
        await fetchLockboxData(lockbox._id);
      }
      message.success('Owner unassigned; defaulted to MLS');
    } catch (err) {
      setValue(prevOwnerValue.current); // Restore the previous value when unassign fails
      handleError(err);
    }
  };

  const reassignLockbox = async (newValue: string) => {
    try {
      const selectedValue = data.find((d: any) => d.id === newValue);
      await updateOwnerOrAssignee({
        serialNumber: lockbox.serialNumber,
        ownerId: newValue,
        ownerType: selectedValue.type,
        updateType: 'assign',
      });
      message.success('Owner assigned');
      const parsedValue = SearchLabel(selectedValue, isMlsUser);
      setValue({ id: newValue, label: parsedValue, type: selectedValue.type });
      if (lockbox._id) {
        await fetchLockboxData(lockbox._id);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleConfirmUnassignOk = async () => {
    setLoading(true);
    await unassignLockbox();
    setConfirmVisible(false);
    setLoading(false);
  };

  const handleConfirmUnassignCancel = () => {
    setValue(prevOwnerValue.current); // Restore the previous value when the user cancels the confirmation dialog
    prevOwnerValue.current = null;
    setConfirmVisible(false);
  };

  const handleConfirmReassignOk = async () => {
    setLoading(true);
    await reassignLockbox(newOwnerValue.current);
    setLoading(false);
    setConfirmReassignVisible(false);
  };

  const handleConfirmReassignCancel = () => {
    setValue(prevOwnerValue.current); // Restore the previous value when the user cancels the confirmation dialog
    prevOwnerValue.current = null;
    setConfirmReassignVisible(false);
  };

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (
        !searchTerm ||
        (featureEnabled('improvedLockboxAssignmentFeature') && !ownerType)
      )
        return;
      setLoading(true);
      setData([]);

      let queryString = `serialNumber=${lockbox.serialNumber}&searchTerm=${searchTerm}&includeUserMlsId=1&includeOfficeId=1&includeOfficeName=1&size=10&page=0`;
      if (featureEnabled('improvedLockboxAssignmentFeature')) {
        queryString += `&type=${ownerType}`;
      }
      if (featureEnabled('membershipKeyholderIdSupportFeature', 1)) {
        queryString += `&includeUserKeyholderId=1`;
      }

      const {
        data: { searchResult },
      } = await Axios.get(`/lockbox/owner_and_assignee?${queryString}`);

      setData(searchResult);
      setLoading(false);
    },
    [lockbox.serialNumber, ownerType],
  );

  const debouncedError = useMemo(
    () => debounce((msg: string) => message.error(msg), 500),
    [],
  );

  const debounceHandleSearch = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch],
  );

  const handleChange = async (newValue: string) => {
    if (!newValue) {
      setValue(null);
      return;
    }

    // Confirm for reassignment
    if (value) {
      prevOwnerValue.current = value; // Save the previous value before showing the confirmation dialog
      newOwnerValue.current = newValue;
      setConfirmReassignVisible(true);
      return;
    }

    await reassignLockbox(newValue);
  };

  const handleClear = () => {
    if (value) {
      prevOwnerValue.current = value; // Save the previous value before showing the confirmation dialog
    } else {
      setValue(null);
    }
    setConfirmVisible(true);
  };

  return (
    <>
      <StyledSelectContainer>
        {featureEnabled('improvedLockboxAssignmentFeature') && (
          <Select
            virtual={false}
            value={ownerType}
            onChange={v => setOwnerType(v)}
            placeholder="Select type"
            style={{ width: 100 }}
            size="large"
            data-cy="owner-type-select"
            options={[
              { value: 'user', label: 'User' },
              { value: 'office', label: 'Office' },
            ]}
          />
        )}
        {!featureEnabled('improvedLockboxAssignmentFeature') && (
          <StyledSearchIcon type="search" width="16px" />
        )}
        <StyledSelect
          virtual={false}
          showSearch
          value={value?.id}
          placeholder={
            featureEnabled('improvedLockboxAssignmentFeature')
              ? 'Search'
              : 'Add an owner'
          }
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={searchTerm => {
            if (
              featureEnabled('improvedLockboxAssignmentFeature') &&
              !ownerType
            ) {
              debouncedError(
                'Please select a type before searching for an owner.',
              );
              return;
            }
            debounceHandleSearch(searchTerm);
          }}
          onChange={handleChange}
          onClear={handleClear}
          notFoundContent={loading ? <Loading /> : null}
          size="large"
          allowClear={
            // Non-MlsUsers cannot unassign lockbox owner
            featureEnabled('LockboxSlowAssignmentEnhancements')
              ? isMlsUser
              : true
          }
          options={(data || []).map((d: any) => ({
            value: d?.id,
            label: SearchLabel(d, isMlsUser),
          }))}
        />
      </StyledSelectContainer>
      {/* Confirm unassignment modal */}
      <Modal
        onHide={handleConfirmUnassignCancel}
        show={confirmVisible}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Unassign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {buildLockboxAssignConfirmMessage({
            action: 'Unassigning',
            ownerType: lockbox?.ownerType as OwnerAndAssigneeType,
            assigneeType: lockbox?.assigneeType as OwnerAndAssigneeType,
            isAssignee: false,
            listingAddress: lockbox.listing?.line1
              ? `${lockbox.listing.line1} ${lockbox.listing?.line2 ?? ''}`.trim()
              : undefined,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleConfirmUnassignCancel}
            style={{ marginRight: '10px' }}
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmUnassignOk}
            loading={loading}
            style={{ color: '#fff' }}
          >
            Unassign
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm reassignment modal */}
      <Modal
        onHide={handleConfirmReassignCancel}
        show={confirmReassignVisible}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reassign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {buildLockboxAssignConfirmMessage({
            action: 'Reassigning',
            ownerType: lockbox?.ownerType as OwnerAndAssigneeType,
            assigneeType: lockbox?.assigneeType as OwnerAndAssigneeType,
            isAssignee: false,
            listingAddress: lockbox.listing?.line1
              ? `${lockbox.listing.line1} ${lockbox.listing?.line2 ?? ''}`.trim()
              : undefined,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleConfirmReassignCancel}
            style={{ marginRight: '10px' }}
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmReassignOk}
            loading={loading}
            style={{ color: '#fff' }}
          >
            Reassign
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const StyledSelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledSearchIcon = styled(Icon)`
  position: absolute;
  left: 10px;
  top: 12px;
  z-index: 1;
  color: ${theme.gray8};
`;

const StyledSelect = styled(Select)`
  width: 425px;
  min-width: 280px;
  display: flex;
  position: relative;
  align-items: center;
  .ant-select-selection-item {
    padding: 0 20px !important;
  }
  .ant-select-selection-search {
    padding: 0px 25px !important;
  }
  .ant-select-selection-placeholder {
    padding: 0px 25px !important;
  }
`;

export default OwnerSearchBar;
